<template>
  <v-dialog v-model="dialog.open" max-width="600px">
    <v-card>
      <v-card-title class="headline">
        {{ title }}
      </v-card-title>

      <v-card-text>
        <div class="mb-4" v-if="showDate">
          <dateAndInputForm
            v-model="form.dueDate"
            :label="$t('managedInstructions.managedInstructionsDueTo')"
            data-cy="dueDate"
            prependIcon="event"
            :rules="[]"
          />
          <div class="error--text caption" v-if="errors.dueDate">
            {{ errors.dueDate }}
          </div>
        </div>
        <div>
          <div class="subtitle-1 mb-2">
            {{ $t("employee.uploadFileForRole", { role: roleLabel }) }}
          </div>
          <input
            ref="fileInput"
            type="file"
            multiple
            class="mt-2"
            @change="handleFiles"
            accept="
    .doc,
    .docx,
    .pdf,
    .ppt,
    .pptx,
    .mp4,
    .mkv,
    .mov
  "
          />
          <div class="error--text caption mt-1" v-if="errors.files">
            {{ errors.files }}
          </div>
          <div class="mt-3" aria-live="polite">
            <div class="caption" v-if="form.files && form.files.length">
              {{ $t("general.filesAdded", { count: form.files.length }) }}
            </div>
            <ul v-if="form.files && form.files.length" class="pl-4">
              <li v-for="(f, i) in form.files" :key="f.name + i">
                {{ f.name }}
                <v-icon small @click="removeFile(i)" data-cy="delete">
                  delete
                </v-icon>
              </li>
            </ul>
          </div>
        </div>
      </v-card-text>

      <v-card-actions>
        <div class="flex-grow-1"></div>
        <v-btn color="red" outlined @click="onDelete" v-if="hasSelectedRole">
          {{ $t("employee.deleteRole", { role: roleLabel }) }}
        </v-btn>
        <v-btn color="grey" outlined @click="onCancel">
          {{ $t("general.cancel") }}
        </v-btn>
        <v-btn color="primary" @click="onSave" data-cy="save-meta">
          {{ $t("general.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// import singleDateForm from "../../general/singleDateForm.vue";
import dateAndInputForm from "../../general/dateAndInputForm.vue";

export default {
  name: "uploadDialog",
  props: {
    dialog: {
      type: Object,
      required: true,
    },
    item: {
      type: Object,
      required: true,
    },
  },
  components: {
    // singleDateForm,
    dateAndInputForm,
  },
  data() {
    return {
      form: {
        dueDate: null,
        files: [],
      },
      errors: {
        dueDate: null,
        files: null,
      },
    };
  },
  computed: {
    roleLabel() {
      // Prefer the label passed in by the caller (covers custom roles).
      if (this.dialog.roleLabel) return this.dialog.roleLabel;

      const role = this.dialog.role;
      if (role === "safetyOfficer") return this.$t("employee.safetyOfficer");
      if (role === "fireMarshal") return this.$t("employee.fireMarshal");
      if (role === "firstAider") return this.$t("employee.firstAider");
      return "";
    },
    // Whether the due-date field is shown. Defaults to true; safetyOfficer and
    // any caller that sets showDate=false hide it.
    showDate() {
      if (typeof this.dialog.showDate === "boolean") return this.dialog.showDate;
      return this.dialog.role !== "safetyOfficer";
    },
    requireDate() {
      return !!this.dialog.requireDate;
    },
    requireFile() {
      // Default true preserves the original behaviour for built-in roles.
      return this.dialog.requireFile !== false;
    },
    title() {
      const name = this.item?.name || "";
      const roleLabel = this.roleLabel;

      if (!roleLabel) return name || "";
      return name ? `${roleLabel} – ${name}` : roleLabel;
    },
    hasSelectedRole() {
      if (!this.dialog.role) return false;
      const roles = Array.isArray(this.item?.roles) ? this.item.roles : [];
      return roles.some((r) => r.role === this.dialog.role);
    },
  },
  watch: {
    "dialog.open"(val) {
      if (val) {
        this.resetForm();
      }
    },
  },
  methods: {
    validate() {
      let valid = true;

      if (!this.form.dueDate && this.requireDate) {
        this.errors.dueDate = this.$t("general.fieldRequired");
        valid = false;
      } else {
        this.errors.dueDate = null;
      }

      if (this.requireFile && (!this.form.files || this.form.files.length === 0)) {
        this.errors.files = this.$t("general.fieldRequired");
        valid = false;
      } else {
        this.errors.files = null;
      }

      return valid;
    },
    resetForm() {
      this.form = {
        dueDate: null,
        files: [],
      };
      this.errors = {
        dueDate: null,
        files: null,
      };
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },
    async handleFiles(e) {
      const fileList = Array.from(e.target.files || []);
      if (!fileList.length) return;

      const toBase64 = (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (ev) => {
            const base64 = String(ev.target.result).split(",")[1];
            const ext =
              (file.name.match(/\.[0-9a-z]+$/i)?.[0] || "").replace(".", "") ||
              file.type ||
              "";
            resolve({
              name: file.name,
              filetype: ext,
              content: base64,
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

      const encoded = await Promise.all(fileList.map(toBase64));

      if (!Array.isArray(this.form.files)) {
        this.form.files = [];
      }
      this.form.files.push(...encoded);

      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },
    removeFile(i) {
      this.form.files.splice(i, 1);
    },
    onCancel() {
      this.dialog.open = false;
    },
    onSave() {
      if (!this.validate()) return;
      this.$emit("triggerSaveUpload", {
        role: this.dialog.role,
        employeeId: this.item?._id,
        dueDate: this.showDate ? this.form.dueDate || "" : "",
        files: this.form.files,
      });
      this.dialog.open = false;
    },
    onDelete() {
      this.$emit("triggerDeleteRole", {
        _id: this.item?._id,
        role: this.dialog.role,
      });
      this.dialog.open = false;
    },
  },
};
</script>
